

import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const useReviews = (productId) => {
  const [reviews, setReviews] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReviews();
  }, [productId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/products/${productId}/reviews`
      );
      if (response.data.success) {
        setReviews(response.data.data.reviews);
        setStatistics(response.data.data.statistics);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const addReview = async (reviewData) => {
    try {
      const response = await axios.post(
        `${API_URL}/products/${productId}/reviews`,
        reviewData
      );
      
      if (response.data.success) {
        // Reload reviews to get updated list
        await loadReviews();
        return { success: true, review: response.data.data };
      }
    } catch (err) {
      console.error('Error adding review:', err);
      return { 
        success: false, 
        error: err.response?.data?.message || 'Failed to submit review' 
      };
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      const response = await axios.delete(`${API_URL}/reviews/${reviewId}`);
      
      if (response.data.success) {
        await loadReviews();
        return { success: true };
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      return { success: false };
    }
  };

  const markHelpful = async (reviewId, helpful) => {
    try {
      const response = await axios.post(
        `${API_URL}/reviews/${reviewId}/helpful`,
        { helpful }
      );
      
      if (response.data.success) {
        await loadReviews();
        return { success: true };
      }
    } catch (err) {
      console.error('Error marking helpful:', err);
      return { 
        success: false,
        error: err.response?.data?.message 
      };
    }
  };

  const getAverageRating = () => {
    return statistics?.average_rating || 0;
  };

  const getRatingDistribution = () => {
    if (!statistics) return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    
    return {
      5: parseInt(statistics.five_star) || 0,
      4: parseInt(statistics.four_star) || 0,
      3: parseInt(statistics.three_star) || 0,
      2: parseInt(statistics.two_star) || 0,
      1: parseInt(statistics.one_star) || 0,
    };
  };

  return {
    reviews,
    loading,
    error,
    addReview,
    deleteReview,
    markHelpful,
    getAverageRating,
    getRatingDistribution,
    totalReviews: statistics?.total_reviews || 0,
  };
};

export default useReviews;